import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { getSettingsRouteAccessScope } from './configAreaBySettingsRoute'
import { hasConfigPageAccess } from './hasConfigPageAccess'
import { useManagedConfigAreas } from './useManagedConfigAreas'
import type { ConfigArea } from '@omnea-app/authz'

export type SettingsAccess = {
  /** Whether the config-permission dimension passes for the given settings route. */
  hasConfigAccess: (route: string | undefined) => boolean
  /** True while managed areas are still loading — callers should hold off redirecting. */
  isLoading: boolean
}

const NO_AREAS: ReadonlySet<ConfigArea> = new Set()

export const hasFgaConfigAccess = (
  route: string | undefined,
  managedAreas: ReadonlySet<ConfigArea>,
  canManagePermissions: boolean
): boolean => {
  const scope = getSettingsRouteAccessScope(route)
  if (scope === 'personal') return true
  if (scope === 'permission-management') return canManagePermissions
  if (scope == null) return false
  return managedAreas.has(scope)
}

/**
 * Unified config-access check for settings tabs and pages. With FGA_POC off this is
 * the existing role-based check (hasConfigPageAccess, including the P2P_ADMIN_MODE stop-gap);
 * with it on, access follows the config areas the user can manage per the permissions service.
 * Other gates (isAdmin, isOmnean, feature flags) apply on top at the call sites.
 */
export const useSettingsAccess = (): SettingsAccess => {
  const { user } = useAuthenticatedUser()
  const featureFlags = useFeatureFlags()
  const fgaConfigAreasEnabled = featureFlags.FGA_POC
  // Deliberately not gated on isAdmin: non-admins may be granted managed config areas in future.
  const { managedAreas, canManagePermissions } = useManagedConfigAreas(fgaConfigAreasEnabled)

  if (!fgaConfigAreasEnabled) {
    return {
      hasConfigAccess: (route) => hasConfigPageAccess({ hasConfigPermissions: user.hasConfigPermissions, route, featureFlags }),
      isLoading: false,
    }
  }

  return {
    hasConfigAccess: (route) => hasFgaConfigAccess(route, managedAreas ?? NO_AREAS, canManagePermissions),
    isLoading: managedAreas == null,
  }
}
